
import { useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { groupsApi, reportsApi, activitiesApi } from '../../../../lib/api';
import ReportModal from '../../../../components/base/ReportModal';

interface GroupModerationToolsProps {
  groupId: string;
  postId?: string;
  postAuthorId?: string;
  memberId?: string;
  memberRole?: string;
  currentUserRole: string;
  onActionComplete?: () => void;
}

export default function GroupModerationTools({
  groupId,
  postId,
  postAuthorId,
  memberId,
  memberRole,
  currentUserRole,
  onActionComplete
}: GroupModerationToolsProps) {
  const { user } = useAuth();
  const [showReportModal, setShowReportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin';
  const isModerator = currentUserRole === 'moderator';
  const canModerate = isOwner || isAdmin || isModerator;

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    const bgColor = type === 'success' 
      ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
      : 'bg-gradient-to-r from-red-500 to-rose-500';
    
    const notification = document.createElement('div');
    notification.className = `fixed bottom-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<i class="ri-${type === 'success' ? 'check' : 'error-warning'}-line mr-2"></i>${message}`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-y-2');
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  };

  // Pin/Unpin Post
  const handlePinPost = async (isPinned: boolean) => {
    if (!postId || !canModerate) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.pinPost(groupId, postId, isPinned);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to pin/unpin post');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: isPinned ? 'group_post_pinned' : 'group_post_unpinned',
        title: isPinned ? 'Pinned a post' : 'Unpinned a post',
        data: { group_id: groupId, post_id: postId }
      });

      showNotification(isPinned ? 'Post pinned' : 'Post unpinned');
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error pinning post:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to pin/unpin post', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Remove Post
  const handleRemovePost = async () => {
    if (!postId || !canModerate) return;
    
    if (!confirm('Are you sure you want to remove this post?')) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.deleteGroupPost(groupId, postId);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove post');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: 'group_post_removed',
        title: 'Removed a post',
        data: { group_id: groupId, post_id: postId }
      });

      showNotification('Post removed successfully');
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error removing post:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to remove post', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Mute Member
  const handleMuteMember = async () => {
    if (!memberId || !canModerate) return;
    
    if (!confirm('Are you sure you want to mute this member? They will not be able to post or comment.')) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.muteMember(groupId, memberId);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to mute member');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: 'group_member_muted',
        title: 'Muted a member',
        data: { group_id: groupId, member_id: memberId }
      });

      showNotification('Member muted successfully');
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error muting member:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to mute member', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Ban Member (Admin/Owner only)
  const handleBanMember = async () => {
    if (!memberId || (!isOwner && !isAdmin)) return;
    
    if (!confirm('Are you sure you want to ban this member? They will be removed from the group and cannot rejoin.')) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.banMember(groupId, memberId);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to ban member');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: 'group_member_banned',
        title: 'Banned a member',
        data: { group_id: groupId, member_id: memberId }
      });

      showNotification('Member banned successfully');
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error banning member:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to ban member', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Promote Member (Owner/Admin only)
  const handlePromoteMember = async (newRole: 'admin' | 'moderator') => {
    if (!memberId || (!isOwner && !isAdmin)) return;
    
    if (!confirm(`Are you sure you want to promote this member to ${newRole}?`)) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.updateMemberRole(groupId, memberId, newRole);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to promote member');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: 'group_member_promoted',
        title: `Promoted a member to ${newRole}`,
        data: { group_id: groupId, member_id: memberId, new_role: newRole }
      });

      showNotification(`Member promoted to ${newRole}`);
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error promoting member:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to promote member', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Demote Member (Owner only)
  const handleDemoteMember = async () => {
    if (!memberId || !isOwner) return;
    
    if (!confirm('Are you sure you want to demote this member to regular member?')) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await groupsApi.updateMemberRole(groupId, memberId, 'member');
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to demote member');
      }

      // Create activity log
      await activitiesApi.createActivity({
        activityType: 'group_member_demoted',
        title: 'Demoted a member',
        data: { group_id: groupId, member_id: memberId }
      });

      showNotification('Member demoted to regular member');
      onActionComplete?.();
      setShowMenu(false);
    } catch (err: any) {
      console.error('Error demoting member:', err);
      setError(err.message);
      showNotification(err.message || 'Failed to demote member', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!canModerate && !postId) return null;

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          disabled={loading}
        >
          {loading ? (
            <i className="ri-loader-4-line animate-spin text-lg"></i>
          ) : (
            <i className="ri-more-2-fill text-lg"></i>
          )}
        </button>

        {showMenu && (
          <>
            {/* Backdrop to close menu */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)}
            />
            
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              {/* Post Actions */}
              {postId && canModerate && (
                <>
                  <button
                    onClick={() => handlePinPost(true)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    disabled={loading}
                  >
                    <i className="ri-pushpin-line mr-3"></i>
                    Pin Post
                  </button>
                  <button
                    onClick={() => handlePinPost(false)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    disabled={loading}
                  >
                    <i className="ri-unpin-line mr-3"></i>
                    Unpin Post
                  </button>
                  <button
                    onClick={handleRemovePost}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    disabled={loading}
                  >
                    <i className="ri-delete-bin-line mr-3"></i>
                    Remove Post
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                </>
              )}

              {/* Member Actions */}
              {memberId && canModerate && memberId !== user?.id && (
                <>
                  {isModerator && (
                    <button
                      onClick={handleMuteMember}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      disabled={loading}
                    >
                      <i className="ri-volume-mute-line mr-3"></i>
                      Mute Member
                    </button>
                  )}

                  {(isAdmin || isOwner) && memberRole === 'member' && (
                    <>
                      <button
                        onClick={() => handlePromoteMember('moderator')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        disabled={loading}
                      >
                        <i className="ri-shield-star-line mr-3"></i>
                        Promote to Moderator
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => handlePromoteMember('admin')}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          disabled={loading}
                        >
                          <i className="ri-shield-check-line mr-3"></i>
                          Promote to Admin
                        </button>
                      )}
                    </>
                  )}

                  {isOwner && (memberRole === 'admin' || memberRole === 'moderator') && (
                    <button
                      onClick={handleDemoteMember}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      disabled={loading}
                    >
                      <i className="ri-arrow-down-line mr-3"></i>
                      Demote to Member
                    </button>
                  )}

                  {(isAdmin || isOwner) && (
                    <button
                      onClick={handleBanMember}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      disabled={loading}
                    >
                      <i className="ri-forbid-line mr-3"></i>
                      Ban Member
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                </>
              )}

              {/* Report Action - Available to all users */}
              {postId && postAuthorId && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowReportModal(true);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-flag-line mr-3"></i>
                  Report Content
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Report Modal */}
      {postId && postAuthorId && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="group_post"
          contentId={postId}
          reportedUserId={postAuthorId}
          onReportSubmitted={onActionComplete}
        />
      )}
    </>
  );
}
